<template>
  <div class="slide slide-goal-distribution">
    <!-- Header -->
    <div class="slide-header">
      <h2 class="slide-title">{{ data.title }}</h2>
      <div v-if="data.subtitle" class="slide-subtitle">{{ data.subtitle }}</div>
    </div>

    <!-- Goals List -->
    <div class="goals-container">
      <div
        v-for="(goal, index) in data.goals"
        :key="index"
        class="goal-row"
        :style="{ animationDelay: `${index * 0.08}s` }"
      >
        <!-- Goal Info -->
        <div class="goal-info">
          <div class="goal-rank">{{ index + 1 }}</div>
          <div class="goal-details">
            <div class="goal-name">{{ goal.goalName }}</div>
            <div class="goal-team">{{ goal.teamName }}</div>
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="bar-container">
          <div class="stacked-bar">
            <div
              class="bar-segment segment-completed"
              :style="{ width: getCompletedWidth(goal) + '%' }"
              :title="`Completed: ${goal.completedItems}`"
            ></div>
            <div
              class="bar-segment segment-in-progress"
              :style="{ width: getInProgressWidth(goal) + '%' }"
              :title="`In Progress: ${goal.focusItems - goal.completedItems}`"
            ></div>
          </div>
          <div class="bar-labels">
            <span class="label-count">{{ formatNumber(goal.focusItems) }} items</span>
            <span class="label-completion">{{ getCompletionRate(goal) }}% done</span>
          </div>
        </div>

        <!-- People Badge -->
        <div class="people-badge">
          <svg class="people-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="people-count">{{ goal.peopleCount }}</span>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!data.goals || data.goals.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="empty-text">No goals with active focus this quarter</div>
      </div>
    </div>

    <!-- Legend -->
    <div v-if="data.goals && data.goals.length > 0" class="legend">
      <div class="legend-item">
        <div class="legend-color legend-completed"></div>
        <span>Completed</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-in-progress"></div>
        <span>In Progress</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GoalDistributionSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  computed: {
    maxFocusItems() {
      if (!this.data.goals || this.data.goals.length === 0) return 1;
      return Math.max(...this.data.goals.map(g => g.focusItems || 0), 1);
    }
  },
  methods: {
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    getCompletedWidth(goal) {
      if (!goal.focusItems || goal.focusItems === 0) return 0;
      // Width relative to max for visual comparison
      const barWidth = (goal.focusItems / this.maxFocusItems) * 100;
      const completedRatio = goal.completedItems / goal.focusItems;
      return barWidth * completedRatio;
    },
    getInProgressWidth(goal) {
      if (!goal.focusItems || goal.focusItems === 0) return 0;
      const barWidth = (goal.focusItems / this.maxFocusItems) * 100;
      const inProgressRatio = (goal.focusItems - goal.completedItems) / goal.focusItems;
      return barWidth * inProgressRatio;
    },
    getCompletionRate(goal) {
      if (!goal.focusItems || goal.focusItems === 0) return 0;
      return Math.round((goal.completedItems / goal.focusItems) * 100);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-goal-distribution {
  @extend .slide-base;
  background: $paper;
  padding: 80px;
  display: flex;
  flex-direction: column;
}

.slide-header {
  margin-bottom: 48px;
}

.slide-title {
  @extend .serif-headline;
  font-size: 56px;
  color: $ink;
  margin: 0;
}

.slide-subtitle {
  font-family: $font-body;
  font-size: 20px;
  color: $ink-muted;
  margin-top: 12px;
}

.goals-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

.goal-row {
  display: grid;
  grid-template-columns: 280px 1fr 100px;
  gap: 24px;
  align-items: center;
  background: #fff;
  border-radius: 16px;
  padding: 20px 28px;
  box-shadow: $shadow-soft;
  animation: fadeInLeft 0.5s ease backwards;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: $shadow-medium;
  }
}

.goal-info {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.goal-rank {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: $violet-100;
  color: $violet-600;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: $font-body;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}

.goal-details {
  min-width: 0;
}

.goal-name {
  font-family: $font-body;
  font-size: 18px;
  font-weight: 600;
  color: $ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.goal-team {
  font-family: $font-body;
  font-size: 14px;
  color: $ink-muted;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stacked-bar {
  display: flex;
  height: 28px;
  border-radius: 14px;
  overflow: hidden;
  background: $paper-cool;
}

.bar-segment {
  height: 100%;
  transition: width 0.6s ease;

  &.segment-completed {
    background: $emerald;
  }

  &.segment-in-progress {
    background: $violet-400;
  }
}

.bar-labels {
  display: flex;
  justify-content: space-between;
  font-family: $font-body;
  font-size: 13px;
  color: $ink-muted;
}

.label-count {
  font-weight: 600;
  color: $ink;
}

.label-completion {
  color: $emerald;
  font-weight: 500;
}

.people-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba($violet-500, 0.1);
  color: $violet-600;
  padding: 10px 16px;
  border-radius: 20px;
}

.people-icon {
  flex-shrink: 0;
}

.people-count {
  font-family: $font-body;
  font-size: 18px;
  font-weight: 700;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px;
  color: $ink-subtle;
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.5;
}

.empty-text {
  font-family: $font-body;
  font-size: 20px;
}

.legend {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid rgba($ink, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: $font-body;
  font-size: 15px;
  color: $ink-muted;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 6px;

  &.legend-completed {
    background: $emerald;
  }

  &.legend-in-progress {
    background: $violet-400;
  }
}

@keyframes fadeInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
